// src/data/coverage/templateAssets.ts
// ============================================================
// THE SERVED TEMPLATE FILES — one declaration of every workbook path
//
// The workbooks live in public/templates/. Vite copies public/ into dist/ verbatim, so a
// file at public/templates/X.xlsx is served at /templates/X.xlsx on the static deploy with
// no import, no bundling and no transform. That matters for an xlsx: anything that rewrote
// the bytes would hand the client a file that opens as garbage in Excel, which is worse
// than no download at all.
//
// ONE COPY OF EACH ARTIFACT. These files used to sit in demo-assets/templates/ and were
// moved, not copied. The parity test reads them from here, so the bytes a client downloads
// are the same bytes the formulas are asserted against. A second copy would let those two
// drift and nothing would notice.
//
// ALL THREE INSTRUMENTS NOW SHIP A BLANK TEMPLATE AND A COMPLETED EXAMPLE. `templateHref`
// stays nullable on purpose: the card renders example-only rather than a dead download
// link when one is missing, which is how D1 and D2 shipped before their templates existed.
// A fourth instrument arriving without a template needs no code change.
// ============================================================

import type { InstrumentId } from './gapInstruments';

export interface TemplateAsset {
  /** Path to the blank template, or null when it does not exist yet. */
  templateHref: string | null;
  /** Path to the completed ACME example. Every instrument has one. */
  exampleHref: string;
  /** Filename the browser saves the download as. */
  templateFileName: string | null;
  exampleFileName: string;
}

const SERVED_ROOT = '/templates';

const asset = (template: string | null, example: string): TemplateAsset => ({
  templateHref: template ? `${SERVED_ROOT}/${template}` : null,
  exampleHref: `${SERVED_ROOT}/${example}`,
  templateFileName: template,
  exampleFileName: example,
});

/** Keyed on the instrument ids in gapInstruments. Path A carries no workbook. */
export const TEMPLATE_ASSETS: Readonly<Record<Exclude<InstrumentId, 'pathA'>, TemplateAsset>> = {
  D3: asset(
    'D3_Data_Asset_and_Platform_Inventory_TEMPLATE.xlsx',
    'D3_Data_Asset_and_Platform_Inventory_ACME_EXAMPLE.xlsx',
  ),
  D1: asset(
    'D1_Staffing_and_Capacity_Inventory_TEMPLATE.xlsx',
    'D1_Staffing_and_Capacity_Inventory_ACME_EXAMPLE.xlsx',
  ),
  D2: asset(
    'D2_Board_Priorities_Extract_TEMPLATE.xlsx',
    'D2_Board_Priorities_Extract_ACME_EXAMPLE.xlsx',
  ),
  D4: asset(
    'D4_Governance_and_Compliance_Charter_TEMPLATE.xlsx',
    'D4_Governance_and_Compliance_Charter_ACME_EXAMPLE.xlsx',
  ),
};

/** Repo-relative directory the files are tracked in. Used by the parity test. */
export const TEMPLATE_SOURCE_DIR = 'public/templates';
